import { CSSProperties, ReactElement } from 'react';
import { Item } from '@sitecore-jss/sitecore-jss/types/layout/models';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { ErrorBoundary, LoginTypeValue, useLoginType } from '@gn-audio/jss-components';

import { CondensedMenuWrapper } from 'common/condensedMenu/withCondensedMenu';
import { sanitizeId } from 'common/utils/sanitizeUtils';

type SpotWrapperProps = {
  component: ReactElement;
  rendering: ComponentRendering<JSSComponentFields>;
};

type PaddingFields = {
  topPaddingMobile: Field<string>;
  bottomPaddingMobile: Field<string>;
  topPaddingDesktop: Field<string>;
  bottomPaddingDesktop: Field<string>;
  topPaddingTablet: Field<string>;
  bottomPaddingTablet: Field<string>;
};

type LoginTypeFields = {
  loginType?: Field<LoginTypeValue>;
  guestDeniedMessage?: Field<string>;
};

type AnchorFields = {
  spotAnchor?: Field<string>;
};

type DataItemFields = Partial<PaddingFields> &
  LoginTypeFields &
  AnchorFields & {
    [name: string]: Field | Item | Item[] | undefined;
  };

type JSSComponentFieldBase = {
  [name: string]:
    | Field
    | Item
    | Item[]
    | {
        data?: {
          item?: DataItemFields;
        };
      }
    | {
        item?: DataItemFields;
      }
    | undefined;
  data?: {
    item?: DataItemFields;
  };
};

export type JSSComponentFields = JSSComponentFieldBase &
  Partial<PaddingFields> &
  LoginTypeFields &
  AnchorFields;

function isPaddingFields(obj: any): obj is PaddingFields {
  return (
    obj && typeof obj === 'object' && 'topPaddingMobile' in obj && 'bottomPaddingMobile' in obj
  );
}

/**
 * Determines the loginType field to use for wrapper-level visibility gating.
 *
 * The SpotWrapper handles login visibility for most components centrally. However, some components
 * (like ProductFilterModule) need to handle their own visibility logic to show custom guest-denied
 * messages. These components are identified by the presence of a guestDeniedMessage field.
 *
 * @param fields - Component fields from Sitecore, which may contain loginType and guestDeniedMessage.
 *                 Supports both standard datasource structure (top-level fields) and GraphQL
 *                 ComponentQuery structure (nested in data.item).
 *
 * @returns The loginType field for wrapper-level gating, or undefined if the component handles
 *          its own visibility (has guestDeniedMessage). Returning undefined skips wrapper gating,
 *          allowing the component to render and manage login visibility internally.
 */
function getLoginTypeForWrapper(
  fields: JSSComponentFields | undefined
): Field<LoginTypeValue> | undefined {
  const guestDeniedMessage = fields?.guestDeniedMessage || fields?.data?.item?.guestDeniedMessage;
  const loginType = fields?.loginType ?? fields?.data?.item?.loginType;

  return guestDeniedMessage ? undefined : loginType;
}

function getSpotAnchor(fields: JSSComponentFields | undefined): string | undefined {
  const spotAnchor = fields?.spotAnchor ?? fields?.data?.item?.spotAnchor;
  return spotAnchor?.value?.trim() || undefined;
}

export const SpotWrapper = ({ component, rendering }: SpotWrapperProps) => {
  const loginType = getLoginTypeForWrapper(rendering.fields);
  const spotAnchor = getSpotAnchor(rendering.fields);
  const { isVisible } = useLoginType(loginType);

  if (!isVisible) return null;

  const parsedProps = rendering.fields;

  const paddingFields: Array<keyof PaddingFields> = [
    'topPaddingMobile',
    'bottomPaddingMobile',
    'topPaddingDesktop',
    'bottomPaddingDesktop',
    'topPaddingTablet',
    'bottomPaddingTablet',
  ];

  const paddingProps =
    parsedProps?.data && 'item' in parsedProps.data && parsedProps.data.item
      ? isPaddingFields(parsedProps.data.item)
        ? parsedProps.data.item
        : parsedProps
      : parsedProps;

  const hasCustomPaddings =
    paddingProps && paddingFields.some((field) => paddingProps[field]?.value);

  let topPaddingMobile, topPaddingTablet, topPaddingDesktop;
  let bottomPaddingMobile, bottomPaddingTablet, bottomPaddingDesktop;

  if (hasCustomPaddings) {
    topPaddingMobile = paddingProps?.topPaddingMobile?.value;
    topPaddingTablet = paddingProps?.topPaddingTablet?.value || topPaddingMobile;
    topPaddingDesktop = paddingProps?.topPaddingDesktop?.value || topPaddingTablet;

    bottomPaddingMobile = paddingProps?.bottomPaddingMobile?.value;
    bottomPaddingTablet = paddingProps?.bottomPaddingTablet?.value || bottomPaddingMobile;
    bottomPaddingDesktop = paddingProps?.bottomPaddingDesktop?.value || bottomPaddingTablet;
  }

  const style = {
    '--top-padding-mobile': topPaddingMobile,
    '--top-padding-tablet': topPaddingTablet,
    '--top-padding-desktop': topPaddingDesktop,
    '--bottom-padding-mobile': bottomPaddingMobile,
    '--bottom-padding-tablet': bottomPaddingTablet,
    '--bottom-padding-desktop': bottomPaddingDesktop,
    backfaceVisibility: 'hidden',
  } as CSSProperties;

  const sanitizedSpotAnchor = sanitizeId(spotAnchor ?? '');

  const wrappedComponent = (
    <CondensedMenuWrapper rendering={rendering as ComponentRendering}>
      {component}
    </CondensedMenuWrapper>
  );

  return (
    <ErrorBoundary name={rendering.componentName}>
      {hasCustomPaddings || sanitizedSpotAnchor ? (
        <div
          id={sanitizedSpotAnchor || undefined}
          tabIndex={sanitizedSpotAnchor ? -1 : undefined}
          style={hasCustomPaddings ? style : undefined}
        >
          {wrappedComponent}
        </div>
      ) : (
        wrappedComponent
      )}
    </ErrorBoundary>
  );
};
